// src/context/ui-state-context.tsx
"use client";

import type { ReactNode, Dispatch, SetStateAction } from 'react';
import React, { createContext, useContext, useState, useEffect } from 'react';

interface UIStateContextType {
  isIncognitoMode: boolean;
  setIsIncognitoMode: Dispatch<SetStateAction<boolean>>;
  isMobileSidebarOpen: boolean;
  setIsMobileSidebarOpen: Dispatch<SetStateAction<boolean>>;
  isSidebarCollapsed: boolean;
  setIsSidebarCollapsed: Dispatch<SetStateAction<boolean>>;
  pageSpecificBackground: string | null;
  setPageSpecificBackground: Dispatch<SetStateAction<string | null>>;
}

const UIStateContext = createContext<UIStateContextType | undefined>(undefined);

export const UIStateProvider = ({ children }: { children: ReactNode }) => {
  const [isIncognitoMode, setIsIncognitoMode] = useState<boolean>(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState<boolean>(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState<boolean>(true);
  const [pageSpecificBackground, setPageSpecificBackground] = useState<string | null>(null);

  useEffect(() => {
    const savedSidebarState = localStorage.getItem('sidebarCollapsed') === 'true';
    setIsSidebarCollapsed(savedSidebarState);
  }, []);

  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', String(isSidebarCollapsed));
  }, [isSidebarCollapsed]);

  return (
    <UIStateContext.Provider value={{ 
        isIncognitoMode, 
        setIsIncognitoMode, 
        isMobileSidebarOpen, 
        setIsMobileSidebarOpen,
        isSidebarCollapsed,
        setIsSidebarCollapsed,
        pageSpecificBackground,
        setPageSpecificBackground,
    }}>
      {children}
    </UIStateContext.Provider>
  );
};

export const useUIState = (): UIStateContextType => {
  const context = useContext(UIStateContext);
  if (context === undefined) {
    throw new Error('useUIState must be used within an UIStateProvider');
  }
  return context;
};
